import { useCallback, useMemo, useState } from 'react';

import {
	DEFAULT_FROM_TIME_VALUE,
	MAX_DEFAULT_TILL_TIME_VALUE,
	MIN_DEFAULT_TILL_TIME_VALUE,
	getOverlappingActivityAction,
} from 'common/activeElements/modals/hooks/const';
import { useCreateAuxiliaryAssignment } from 'common/activeElements/modals/hooks/useCreateAuxilaryAssignment';
import { useFormWithTimeInputs } from 'common/activeElements/modals/hooks/useFormWithTimeInput';
import useOverlappingBanner from 'common/OverlappingActivitiesBanner/hooks/useOverlappingBanner';
import { TRAVELLING_ACTIVITY_TYPE } from 'constants/activityTypes';
import { resetTimestampToUtc, reverseFormTime, updateDateWithTimeAndTZ } from 'utils/datetime';

import { DEFAULT_VALUES } from './const';

export const useModalTravelToLocation = ({
	employeeId,
	baseTimezone,
	readOnly,
	initialData,
	onActionSuccess,
	onClose,
}) => {
	const [hasShiftTimeTooLongError, setHasShiftTimeTooLongError] = useState(false);
	const { submit, loading } = useCreateAuxiliaryAssignment();
	const {
		hasOverlappingActivities,
		cannotCancelOverlaps,
		overlappingActivities,
		overlappingAction,
		isDelayOptionAvailable,
		allowToDeleteOverlaps,
		allowToDelayOverlaps,
		delayMinutes,
		delayError,
		minDelayMinutes,
		setDelayMinutes,
		setOverlappingAction,
		setOverlappingActivities,
		resetOverlappingActivities,
	} = useOverlappingBanner(baseTimezone);

	const initialFormValues = useMemo(() => {
		if (!readOnly || !initialData) return DEFAULT_VALUES;

		const time = reverseFormTime(initialData.fromTimestamp, initialData.toTimestamp, baseTimezone);

		return {
			...DEFAULT_VALUES,
			...time,
			fromLocation: {
				key: initialData?.fromStation?.city?.id,
				value: initialData?.fromStation?.city?.id,
				title: initialData?.fromStation?.city?.name,
			},
			toLocation: {
				key: initialData?.toStation?.city?.id,
				value: initialData?.toStation?.city?.id,
				title: initialData?.toStation?.city?.name,
			},
			note: initialData?.note ?? '',
		};
	}, [readOnly, initialData, baseTimezone]);

	const {
		miDay,
		isValid,
		control,
		defineMinDay,
		isValidDate,
		validateYear,
		handleResetForm,
		handleResetSingleField,
		validateTillTime,
		setValue,
		getValues,
		trigger,
		watch,
	} = useFormWithTimeInputs(initialFormValues);

	const resetOverlapsOnChange = useCallback(() => {
		resetOverlappingActivities();
		setHasShiftTimeTooLongError(false);
	}, [resetOverlappingActivities]);

	const buildMutationInput = useCallback(
		(fields) => {
			const { from, till, fromTime, tillTime, note, fromLocation, toLocation } = fields;
			const newFrom = updateDateWithTimeAndTZ(from, fromTime, baseTimezone);
			const newTo = updateDateWithTimeAndTZ(till, tillTime, baseTimezone);

			return {
				crewMemberId: employeeId,
				fromTimestamp: resetTimestampToUtc(newFrom),
				toTimestamp: resetTimestampToUtc(newTo),
				fromCityId: fromLocation?.key ?? fromLocation?.value,
				toCityId: toLocation?.key ?? toLocation?.value,
				activityType: TRAVELLING_ACTIVITY_TYPE,
				...(note ? { note } : {}),
			};
		},
		[employeeId, baseTimezone]
	);

	const handleSubmit = useCallback(async () => {
		const input = buildMutationInput(getValues());
		const overlappingActivityAction = getOverlappingActivityAction({
			allowToDeleteOverlaps,
			allowToDelayOverlaps,
		});

		const result = await submit(input, {
			overlappingActivityAction,
			delayMinutes: allowToDelayOverlaps ? delayMinutes : null,
		});

		if (result.kind === 'success') {
			resetOverlappingActivities();
			setHasShiftTimeTooLongError(false);
			handleResetForm();
			onActionSuccess(result.payload.workAssignment.fromTimestamp);
			onClose();
			return;
		}

		if (result.kind === 'overlap') {
			setHasShiftTimeTooLongError(false);
			setOverlappingActivities(result.list, {
				minDelay: result.minDelayMinutes,
				maxDelay: result.maxDelayMinutes,
			});
			return;
		}

		if (result.kind === 'shift-too-long') {
			resetOverlappingActivities();
			setHasShiftTimeTooLongError(true);
		}
	}, [
		getValues,
		buildMutationInput,
		submit,
		allowToDeleteOverlaps,
		allowToDelayOverlaps,
		delayMinutes,
		resetOverlappingActivities,
		setOverlappingActivities,
		handleResetForm,
		onActionSuccess,
		onClose,
	]);

	const handleClose = useCallback(() => {
		handleResetForm();
		resetOverlappingActivities();
		setHasShiftTimeTooLongError(false);
		onClose();
	}, [handleResetForm, resetOverlappingActivities, onClose]);

	const handleFromDateChange = useCallback(
		(field, e) => {
			const newFromDate = e.target.value;
			const currentTill = getValues().till;

			field.onChange(newFromDate);
			setValue('fromTime', DEFAULT_FROM_TIME_VALUE, { shouldDirty: true, shouldValidate: true });

			if (currentTill && newFromDate > currentTill) {
				setValue('till', newFromDate, { shouldDirty: true, shouldValidate: true });
			}

			if (currentTill) trigger('till');
			defineMinDay();
			resetOverlapsOnChange();
		},
		[getValues, setValue, trigger, defineMinDay, resetOverlapsOnChange]
	);

	const handleFromTimeChange = useCallback(
		(field, e) => {
			field.onChange(e.target.value);
			trigger('tillTime');
			resetOverlapsOnChange();
		},
		[trigger, resetOverlapsOnChange]
	);

	const handleTillDateChange = useCallback(
		(field, e) => {
			field.onChange(e.target.value);

			if (!getValues().tillTime) {
				setValue('tillTime', MAX_DEFAULT_TILL_TIME_VALUE, {
					shouldDirty: true,
					shouldValidate: true,
				});
			} else {
				trigger('tillTime');
			}

			trigger('from');
			resetOverlapsOnChange();
		},
		[getValues, setValue, trigger, resetOverlapsOnChange]
	);

	const handleTillTimeChange = useCallback(
		(field, e) => {
			field.onChange(e.target.value);
			resetOverlapsOnChange();
		},
		[resetOverlapsOnChange]
	);

	const from = watch('from');
	const fromTime = watch('fromTime');
	const till = watch('till');
	const tillTime = watch('tillTime');
	const fromLocation = watch('fromLocation');
	const toLocation = watch('toLocation');

	const minTillTime = useMemo(() => {
		if (from && fromTime && till && from === till) {
			const start = new Date(`${from}T${fromTime}`);

			start.setMinutes(start.getMinutes() + 1);

			return start.toTimeString().slice(0, 5);
		}

		return MIN_DEFAULT_TILL_TIME_VALUE;
	}, [from, fromTime, till]);

	const isFromTimeDisabled = !from;
	const isTillDisabled = !from || !fromTime;
	const isTillTimeDisabled = !from || !fromTime || !till;

	const isSubmitDisabled = useMemo(
		() =>
			!fromLocation ||
			!toLocation ||
			!from ||
			!till ||
			!fromTime ||
			!tillTime ||
			!isValid ||
			hasShiftTimeTooLongError ||
			(hasOverlappingActivities && !allowToDeleteOverlaps && !allowToDelayOverlaps) ||
			(hasOverlappingActivities && cannotCancelOverlaps) ||
			!!delayError,
		[
			fromLocation,
			toLocation,
			from,
			till,
			fromTime,
			tillTime,
			isValid,
			hasShiftTimeTooLongError,
			hasOverlappingActivities,
			allowToDeleteOverlaps,
			allowToDelayOverlaps,
			cannotCancelOverlaps,
			delayError,
		]
	);

	return {
		control,
		miDay,
		minTillTime,
		maxTillTime: MAX_DEFAULT_TILL_TIME_VALUE,
		loading,
		isSubmitDisabled,
		isFromTimeDisabled,
		isTillDisabled,
		isTillTimeDisabled,
		hasOverlappingActivities,
		overlappingActivities,
		overlappingAction,
		isDelayOptionAvailable,
		cannotCancelOverlaps,
		hasShiftTimeTooLongError,
		delayMinutes,
		delayError,
		minDelayMinutes,
		isValidDate,
		validateYear,
		validateTillTime,
		handleFromDateChange,
		handleFromTimeChange,
		handleTillDateChange,
		handleTillTimeChange,
		setOverlappingAction,
		setDelayMinutes,
		handleResetSingleField,
		handleSubmit,
		handleClose,
		setValue,
	};
};
